import { useMemo } from 'react';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { useAirlines } from '@/lib/api/airlines';
import { useAirportCities } from '@/lib/api/airports';
import { cityOf } from '@/types/airport';

/**
 * A flight access in words — "Air Astana flights to or from Astana".
 *
 * Shared by the administrator who sets an access and the user it narrows, because the
 * two must read the same sentence: a description the administrator saw while saving
 * and a different one the user sees on their board would be two answers to one
 * question.
 */

type Translate = (key: Key, values?: Record<string, string | number>) => string;

/** Just the parts of an access a sentence is made from — a saved one or a draft. */
export interface AccessShape {
  restricted: boolean;
  airports: readonly string[];
  airlines: readonly string[];
}

export interface AccessNames {
  /** The city an airport is in, or the code when the dictionary does not know it. */
  airport: (code: string) => string;
  /** A carrier's short name, or the code when the working list does not name it. */
  airline: (code: string) => string;
}

/**
 * Names for the codes in one or more accesses.
 *
 * Pass every code that will be described, memoized: the airport side looks up what
 * the station list does not already know, once per code per session, and a new array
 * on every render would ask it to check again on every render.
 *
 * Airlines come from the working list `/airline/all`, which is already fetched for
 * the board. A carrier off that list keeps its bare code, which is still true.
 */
export const useAccessNames = (airportCodes: readonly string[]): AccessNames => {
  const { locale } = useTranslation();
  const { airlines } = useAirlines();
  const airportOf = useAirportCities(airportCodes);

  const titles = useMemo(() => {
    const byCode = new Map<string, string>();

    for (const airline of airlines) {
      const code = (airline.iataCode ?? '').trim().toUpperCase();
      const title = (airline.title ?? '').trim();
      if (code !== '' && title !== '') byCode.set(code, title);
    }

    return byCode;
  }, [airlines]);

  return {
    airport: (code) => {
      const airport = airportOf(code);
      return (airport ? cityOf(airport, locale) : '') || code;
    },
    airline: (code) => titles.get(code.toUpperCase()) ?? code,
  };
};

/**
 * The sentence.
 *
 * Four shapes, following the rule: no restriction is every flight; airports alone
 * match the route; airlines alone match the carrier; both match both. A restriction
 * with both lists empty is not one the server accepts, and the sentence says what it
 * would mean — nothing — so a draft in that state reads as the problem it is.
 */
export const describeAccess = (access: AccessShape, t: Translate, names: AccessNames): string => {
  if (!access.restricted) return t('flightAccess.all');

  const airports = joinOr(access.airports.map(names.airport), t);
  const airlines = joinOr(access.airlines.map(names.airline), t);

  if (airports !== '' && airlines !== '') return t('flightAccess.both', { airports, airlines });
  if (airports !== '') return t('flightAccess.airportsOnly', { airports });
  if (airlines !== '') return t('flightAccess.airlinesOnly', { airlines });

  return t('flightAccess.incomplete');
};

/**
 * `Almaty, Shymkent or Astana`.
 *
 * Not `Intl.ListFormat`: it formats Kazakh from the CLDR root, the same gap that keeps
 * dates out of `Intl` in this app — see i18n/format.ts.
 */
const joinOr = (items: readonly string[], t: Translate): string => {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} ${t('flightAccess.or')} ${items[items.length - 1] ?? ''}`;
};
